import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';

import {
  faMicrophone,
  faMicrophoneSlash,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';

import { isSilentAtom } from '../../atoms/recording';

type MiniMicVisualizerProps = {
  stream: MediaStream | null;
  barCount?: number;
  className?: string;
  muteThreshold?: number; // default: 0.02
  muteDelayMs?: number; // default: 2000ms
};

export const MiniMicVisualizer: FC<MiniMicVisualizerProps> = ({
  stream,
  barCount = 49,
  className = '',
  muteThreshold = 0.02,
  muteDelayMs = 2000,
}) => {
  const [volume, setVolume] = useState(0);
  const [isMuted, setIsMuted] = useState(true); // Visual mute for bar colors (RMS-based)
  const lastLoudTimeRef = useRef<number>(0);
  const animationRef = useRef<number>();
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const bufferLengthRef = useRef<number>();
  const audioContextRef = useRef<AudioContext>();

  // Global mute state from AudioRecorderProvider (single source of truth)
  const isSilent = useAtomValue(isSilentAtom);

  // The library creates new MediaStream wrapper objects on every render,
  // but the underlying MediaStreamTrack is stable - use it directly
  const audioTrack = stream?.getAudioTracks()[0];
  const isTrackLive = audioTrack?.readyState === 'live';

  useEffect(() => {
    // if there's no active audio track, reset the state of our visualizer and exit early
    if (!audioTrack || !isTrackLive) {
      setVolume(0);
      setIsMuted(true);
      return;
    }

    // create the audio context (used to analyze audio in real-time)
    const audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    audioContextRef.current = audioContext;

    // create an analyser node to process the audio stream
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512; // smaller fftSize = faster + more responsive, but less accurate

    // connect the MediaStreamTrack to the analyser via a new MediaStream
    // Note: This wrapper MediaStream is needed for createMediaStreamSource
    const mediaStream = new MediaStream([audioTrack]);
    const source = audioContext.createMediaStreamSource(mediaStream);
    source.connect(analyser);

    // prepare a buffer to hold the time-domain waveform data
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength) as Uint8Array<ArrayBuffer>;

    analyserRef.current = analyser;
    dataArrayRef.current = dataArray;
    bufferLengthRef.current = bufferLength;

    // Track if cleanup has been called to prevent RAF callback after unmount
    let isCleanedUp = false;

    // the main animation loop that continuously samples mic input
    const update = () => {
      // Exit early if cleanup has been called
      if (isCleanedUp) return;

      const currentAnalyser = analyserRef.current;
      const currentDataArray = dataArrayRef.current;
      const currentBufferLength = bufferLengthRef.current;

      if (
        !currentAnalyser ||
        !currentDataArray ||
        currentBufferLength === undefined
      )
        return;

      // fill dataArray with the current waveform from the analyser
      // Type assertion needed because getByteTimeDomainData expects Uint8Array<ArrayBuffer>
      currentAnalyser.getByteTimeDomainData(
        currentDataArray as Uint8Array<ArrayBuffer>,
      );

      // compute the peak volume and RMS (root mean square) to detect sound level
      let peak = 0;
      let sumSquares = 0;

      // TypeScript guard: currentDataArray is checked above, but we need to assert it's not null
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const safeDataArray: Uint8Array<ArrayBuffer> = currentDataArray!;
      for (let i = 0; i < currentBufferLength; i++) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const v = safeDataArray[i]! - 128; // convert to range around 0
        const normalized = Math.abs(v) / 128;
        peak = Math.max(peak, normalized); // track the loudest single value
        sumSquares += v * v;
      }

      // calculate RMS volume to detect if the input is silent or not
      const rms = Math.sqrt(sumSquares / currentBufferLength) / 128;
      const now = performance.now();

      // For visual indicator (bar color), use RMS threshold
      if (rms >= muteThreshold) {
        lastLoudTimeRef.current = now;
      }
      const isVisuallyMuted = now - lastLoudTimeRef.current > muteDelayMs;

      // update React state for visualization only
      // (actual mute detection is handled by AudioRecorderProvider)
      setVolume(peak);
      setIsMuted(isVisuallyMuted);

      // loop: schedule the next animation frame (only if not cleaned up)
      if (!isCleanedUp) {
        animationRef.current = requestAnimationFrame(update);
      }
    };

    // kick off the loop immediately on mount
    lastLoudTimeRef.current = performance.now();
    update();

    // cleanup when the stream stops or component unmounts
    return () => {
      isCleanedUp = true;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current); // stop loop
      }
      analyser.disconnect();
      source.disconnect();
      // Close the audio context to free system resources
      audioContext.close().catch(() => {
        // Ignore errors if already closed
      });
      // Clear refs to help GC
      analyserRef.current = null;
      dataArrayRef.current = null;
      bufferLengthRef.current = undefined;
    };
  }, [audioTrack, isTrackLive, muteThreshold, muteDelayMs]);

  const barColor = isMuted ? 'vai-bg-red-500' : 'vai-bg-green-500';

  /**
   * Tailwind CSS (even with JIT) requires finite values.
   * Since getBarHeight(i) produces dynamic/continuous values, we need to:
   * - Quantize (round) the height into a discrete set
   * - Map those to predefined Tailwind classes
   */
  const barHeightClasses = [
    'vai-h-[8px]',
    'vai-h-[16px]',
    'vai-h-[24px]',
    'vai-h-[32px]',
    'vai-h-[40px]',
  ];

  const getBarHeightClass = (index: number, currentVolume: number): string => {
    const center = Math.floor(barCount / 2);
    const distance = Math.abs(center - index);
    const scale = 1 - distance / center;
    const effectiveVolume = Math.min(1, currentVolume * scale);
    const heightIndex = Math.round(
      effectiveVolume * (barHeightClasses.length - 1),
    );
    // Ensure heightIndex is within bounds
    const clampedIndex = Math.max(
      0,
      Math.min(heightIndex, barHeightClasses.length - 1),
    );
    // TypeScript should know this is safe, but we'll assert it
    const result = barHeightClasses[clampedIndex];
    if (result === undefined) {
      return barHeightClasses[0] ?? 'vai-h-1';
    }
    return result;
  };

  // When muted, show a large crossed-out mic icon instead of the visualizer
  // Keep the same layout structure so the timer doesn't shift
  if (isSilent) {
    return (
      <div
        className={classNames('vai-flex vai-items-center vai-gap-2', className)}
      >
        {/* Placeholder for mic icon width */}
        <div className="vai-w-4" />

        {/* Quiet icon centered in the bars area with tooltip */}
        <div className="vai-flex vai-items-center vai-justify-center vai-gap-[4px] vai-h-[40px] vai-flex-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="vai-cursor-pointer vai-border-none vai-bg-transparent vai-p-0"
              >
                <FontAwesomeIcon
                  icon={faMicrophoneSlash}
                  className="vai-h-8 vai-w-8 vai-text-amber-500"
                />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                We haven&apos;t detected audio in a while. Check your mic if
                needed.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames('vai-flex vai-items-center vai-gap-2', className)}
    >
      {/* Mic Icon */}
      <FontAwesomeIcon
        icon={faMicrophone}
        className={classNames(
          'vai-w-4 vai-h-4 vai-text-muted-foreground',
          !isMuted && 'vai-animate-pulse vai-text-green-500',
        )}
      />

      {/* Bars */}
      <div className="vai-flex vai-items-center vai-justify-center vai-gap-[4px] vai-h-[40px]">
        {Array.from({ length: barCount }, (_, i) => {
          return (
            <div
              key={i}
              className={classNames(
                'vai-w-[4px] vai-rounded-sm vai-transition-all vai-duration-75 vai-ease-out',
                barColor,
                getBarHeightClass(i, volume),
              )}
            />
          );
        })}
      </div>
    </div>
  );
};
